import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Button, Card, Input, Label, Textarea, Checkbox, Radio, Toggle, Select, Icon, Spinner, Title, Text,
} from '@nimbus-ds/components'
// Using custom slider since Nimbus has no slider component
import {
  ArrowLeftIcon, CogIcon, DisketteIcon, ColorPaletteIcon, CalendarIcon, StepsIcon, ClockIcon,
} from '@nimbus-ds/icons'
import { useToast } from '@/hooks/use-toast'
import type { Experiment, ExperimentFormData } from '@/types'
import { experimentService } from '@/api/experiments'
import { funnelService } from '@/api/funnel'
import { useAuth } from '@/hooks/useAuth'
import { Tier1VisualConfig } from '@/components/experiments/configs/Tier1VisualConfig'
import { RiskLevelSelector } from '@/components/experiments/RiskLevelSelector'
import type { ExperimentConfig } from '@/components/visual-editor/WorkingCopy'
import { VisualSelectorPicker } from '@/components/experiment-builder/VisualSelectorPicker'

export function ExperimentEditPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { toast } = useToast()
  const { user } = useAuth()
  const [experiment, setExperiment] = useState<Experiment | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [formData, setFormData] = useState<Partial<ExperimentFormData>>({})
  const [visualEditorWindow, setVisualEditorWindow] = useState<Window | null>(null)
  const [variantConfig, setVariantConfig] = useState<ExperimentConfig | null>(null)
  const [showSelectorPicker, setShowSelectorPicker] = useState(false)
  const [selectorPickerCallback, setSelectorPickerCallback] = useState<((selector: string) => void) | null>(null)

  const [availableFunnelSteps, setAvailableFunnelSteps] = useState<{ id: string; name: string; order?: number }[]>([])

  // Timing and limits state
  const [enableStartTime, setEnableStartTime] = useState(false)
  const [startDateTime, setStartDateTime] = useState('')
  const [enableEndTime, setEnableEndTime] = useState(false)
  const [endDateTime, setEndDateTime] = useState('')
  const [enableSessionLimit, setEnableSessionLimit] = useState(false)
  const [sessionLimit, setSessionLimit] = useState(0)

  useEffect(() => {
    if (id) {
      loadExperiment()
    }
  }, [id])

  useEffect(() => {
    if (!user?.store_id) return
    funnelService.getFunnelConfig(user.store_id)
      .then(config => setAvailableFunnelSteps(
        (config.steps || []).map(s => ({ id: s.id, name: s.name, order: s.order }))
      ))
      .catch(err => console.warn('[EditPage] Failed to load funnel steps:', err.message))
  }, [user?.store_id])

  const loadExperiment = async () => {
    try {
      setLoading(true)
      if (id) {
        const data = await experimentService.getExperiment(id)
        setExperiment(data)
        // Transform experiment data to form data
        setFormData({
          name: data.name,
          objective: data.objective,
          hypothesis: data.hypothesis,
          mde: data.mde ?? null,
          primarySegment: data.primary_segment ?? 'overall',
          riskLevel: data.risk_level ?? 'balanced',
          hardToRevert: data.hard_to_revert === true,
          type: data.type,
          isNegative: data.is_negative === true,
          controlTraffic: data.traffic_split?.control || 50,
          variantTraffic: data.traffic_split?.treatment || 50,
          urlPatterns: data.targeting?.url_patterns || '',
          devices: data.targeting?.device_conditions || {
            desktop: true,
            mobile: true,
          },
          exclusions: data.targeting?.exclusions || '',
          funnelOption: data.funnel_config?.use_default ? 'default' : 'custom',
          funnelSteps: data.funnel_config?.selected_steps || [],
        })

        // Load timing configuration
        if (data.timing?.start_date_time) {
          setEnableStartTime(true)
          setStartDateTime(new Date(data.timing.start_date_time).toISOString().slice(0, 16))
        }
        if (data.timing?.end_date_time) {
          setEnableEndTime(true)
          setEndDateTime(new Date(data.timing.end_date_time).toISOString().slice(0, 16))
        }

        // Load session limit
        if (data.session_limit) {
          setEnableSessionLimit(true)
          setSessionLimit(data.session_limit)
        }
      }
    } catch (error) {
      console.error('Failed to load experiment:', error)
      toast({
        title: 'Error',
        description: 'No se pudo cargar el experimento.',
        variant: 'destructive',
      })
    } finally {
      setLoading(false)
    }
  }

  const handleSave = async () => {
    if (!experiment || !id) return

    try {
      setSaving(true)

      const updatedData: any = {
        name: formData.name || experiment.name,
        objective: formData.objective || experiment.objective,
        hypothesis: formData.hypothesis || experiment.hypothesis,
        mde: formData.mde ?? null,
        primary_segment: formData.primarySegment ?? 'overall',
        risk_level: formData.riskLevel ?? 'balanced',
        hard_to_revert: formData.hardToRevert === true,
        is_negative: formData.isNegative === true,
        traffic_split: {
          control: formData.controlTraffic || 50,
          treatment: formData.variantTraffic || 50,
        },
        targeting: {
          url_patterns: formData.urlPatterns || '',
          device_conditions: formData.devices || {
            desktop: true,
            mobile: true,
          },
          exclusions: formData.exclusions || '',
        },
      }

      // Include variant config if it's been modified
      if (formData.variantConfig) {
        updatedData.variants = {
          control: experiment.variants?.control || { name: 'Control', description: 'Control', config: {} },
          treatment: {
            name: experiment.variants?.treatment?.name || 'Variante',
            description: experiment.variants?.treatment?.description || 'Tratamiento',
            config: formData.variantConfig,
          },
        }
      }

      // Include funnel configuration
      if (formData.funnelOption) {
        updatedData.funnel_config = {
          use_default: formData.funnelOption === 'default',
          selected_steps: formData.funnelOption === 'custom' ? (formData.funnelSteps || []) : [],
          custom_steps: [],
        }
      }

      // Include timing configuration
      const timing: any = {}
      if (enableStartTime && startDateTime) {
        timing.start_date_time = new Date(startDateTime).toISOString()
      }
      if (enableEndTime && endDateTime) {
        timing.end_date_time = new Date(endDateTime).toISOString()
      }
      if (Object.keys(timing).length > 0) {
        updatedData.timing = timing
      }

      // Include session limit
      if (enableSessionLimit && sessionLimit) {
        updatedData.session_limit = sessionLimit
      }

      await experimentService.updateExperiment(id, updatedData)

      toast({
        title: 'Experimento actualizado',
        description: 'Los cambios se han guardado exitosamente.',
      })

      navigate(`/dashboard/experiments/${id}`)
    } catch (error) {
      console.error('Failed to update experiment:', error)
      toast({
        title: 'Error',
        description: 'No se pudieron guardar los cambios.',
        variant: 'destructive',
      })
    } finally {
      setSaving(false)
    }
  }

  const handleLaunchVisualEditor = () => {
    // Get store URL from experiment or use placeholder
    const storeUrl = experiment?.targeting?.url_patterns || 'https://ejemplo.mitiendanube.com'

    // Open store in new window
    const editorWindow = window.open(storeUrl, 'VisualEditor', 'width=1400,height=900')

    if (editorWindow) {
      setVisualEditorWindow(editorWindow)

      // Wait for window to load, then inject visual editor
      editorWindow.addEventListener('load', () => {
        // Inject visual editor script
        const script = editorWindow.document.createElement('script')
        script.src = `${window.location.origin}/visual-editor.js`
        script.onload = () => {
          // Initialize editor after script loads
          setTimeout(() => {
            if ((editorWindow as any).TNXReactEditor) {
              (editorWindow as any).TNXReactEditor.init()
            }
          }, 500)
        }
        editorWindow.document.head.appendChild(script)

        // Inject CSS
        const link = editorWindow.document.createElement('link')
        link.rel = 'stylesheet'
        link.href = `${window.location.origin}/visual-editor.css`
        editorWindow.document.head.appendChild(link)
      })

      toast({
        title: 'Editor Visual Abierto',
        description: 'El editor visual se está cargando en una nueva ventana.',
      })
    } else {
      toast({
        title: 'Error',
        description: 'No se pudo abrir el editor visual. Verifica que los pop-ups estén habilitados.',
        variant: 'destructive',
      })
    }
  }

  const handlePickSelector = (callback: (selector: string) => void) => {
    setSelectorPickerCallback(() => callback)
    setShowSelectorPicker(true)
  }

  const handleSelectorSelected = (selector: string) => {
    if (selectorPickerCallback) {
      selectorPickerCallback(selector)
    }
    setShowSelectorPicker(false)
    setSelectorPickerCallback(null)
  }

  const handleSelectorPickerCancel = () => {
    setShowSelectorPicker(false)
    setSelectorPickerCallback(null)
  }

  const handleVariantConfigSaved = (config: ExperimentConfig) => {
    setVariantConfig(config)
    toast({
      title: 'Variante Guardada',
      description: `Se guardó la configuración para el selector: ${config.selector}`,
    })

    // Auto-save to experiment
    if (experiment && id) {
      const updatedExperiment = {
        ...experiment,
        variants: {
          ...experiment.variants,
          treatment: {
            name: 'Visual Editor Variant',
            description: `Visual modifications for ${config.selector}`,
            config: config,
          },
        }
      }

      // Save to backend
      experimentService.updateExperiment(id, updatedExperiment).then(() => {
        toast({
          title: 'Guardado',
          description: 'La variante se guardó en el experimento.',
        })
      }).catch((error) => {
        console.error('Failed to save variant:', error)
        toast({
          title: 'Error',
          description: 'No se pudo guardar la variante.',
          variant: 'destructive',
        })
      })
    }
  }

  // Listen for postMessage from visual editor
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.data.type === 'TNX_VISUAL_EDITOR_SAVE') {
        handleVariantConfigSaved(event.data.config)

        // Send success response
        if (visualEditorWindow) {
          visualEditorWindow.postMessage({
            type: 'TNX_SAVE_SUCCESS',
            variantId: id,
          }, '*')
        }
      } else if (event.data.type === 'TNX_EDITOR_CLOSED') {
        setVisualEditorWindow(null)
      }
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [visualEditorWindow, experiment, id])

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse">
          <div className="h-8 w-48 rounded bg-muted mb-2"></div>
          <div className="h-4 w-64 rounded bg-muted"></div>
        </div>
        <Card>
          <div className="animate-pulse">
            <Card.Header>
              <div className="h-6 w-1/3 rounded bg-muted"></div>
              <div className="h-4 w-1/2 rounded bg-muted"></div>
            </Card.Header>
            <Card.Body>
              <div className="space-y-4">
                <div className="h-4 w-full rounded bg-muted"></div>
                <div className="h-4 w-3/4 rounded bg-muted"></div>
              </div>
            </Card.Body>
          </div>
        </Card>
      </div>
    )
  }

  if (!experiment) {
    return (
      <div className="space-y-6">
        <Button
          appearance="transparent"
          onClick={() => navigate('/dashboard/experiments')}
          className="mb-4"
        >
          <Icon source={<ArrowLeftIcon />} color="currentColor" />
          Volver a experimentos
        </Button>
        <Card>
          <Card.Body>
            <div className="py-12 text-center">
              <Title as="h3" className="mb-2">Experimento no encontrado</Title>
              <Text>
                El experimento que buscas no existe o no tienes permisos para editarlo.
              </Text>
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  const renderVariantConfig = () => {
    // Get the actual variant config from treatment variant
    const treatmentConfig = experiment.variants?.treatment?.config || formData.variantConfig || {}

    switch (experiment.type) {
      case 'tier1_visual':
        return (
          <Tier1VisualConfig
            config={treatmentConfig as any}
            onChange={(config) => setFormData(prev => ({ ...prev, variantConfig: config }))}
            onLaunchEditor={handleLaunchVisualEditor}
          />
        )
      default:
        return (
          <div className="text-center py-8 text-muted-foreground">
            Configuración avanzada para tipo {experiment.type} próximamente
          </div>
        )
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <Button
            appearance="transparent"
            onClick={() => navigate(`/dashboard/experiments/${id}`)}
            className="mb-4"
          >
            <Icon source={<ArrowLeftIcon />} color="currentColor" />
            Volver a detalles
          </Button>
          <div className="flex items-center gap-3">
            <Icon source={<CogIcon />} />
            <div>
              <Title as="h1" className="tracking-tight">Editar Experimento</Title>
              <Text>{experiment.name}</Text>
            </div>
          </div>
        </div>
        <Button appearance="primary" onClick={handleSave} disabled={saving}>
          {saving ? (
            <>
              <Spinner size="small" />
              Guardando...
            </>
          ) : (
            <>
              <Icon source={<DisketteIcon />} color="currentColor" />
              Guardar Cambios
            </>
          )}
        </Button>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* Basic Information */}
        <Card>
          <Card.Header>
            <Title as="h3">Información Básica</Title>
            <Text>
              Detalles principales del experimento
            </Text>
          </Card.Header>
          <Card.Body>
           <div className="space-y-4">
            <div>
              <Label htmlFor="name">Nombre del Experimento</Label>
              <Input
                id="name"
                value={formData.name || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ej: Botón CTA Verde vs Naranja"
              />
            </div>
            <div>
              <Label htmlFor="objective">Objetivo</Label>
              <Input
                id="objective"
                value={formData.objective || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, objective: e.target.value }))}
                placeholder="Ej: Aumentar clicks en botón de compra"
              />
            </div>
            <div>
              <Label htmlFor="hypothesis">Hipótesis</Label>
              <Textarea
                id="hypothesis"
                value={formData.hypothesis || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, hypothesis: e.target.value }))}
                placeholder="Ej: Un botón verde será más llamativo..."
                lines={3}
              />
            </div>
            <div>
              <Label htmlFor="mde">
                Efecto mínimo detectable (MDE){' '}
                <span className="text-muted-foreground font-normal">— opcional</span>
              </Label>
              <div className="flex items-center gap-2">
                <Input
                  id="mde"
                  type="number"
                  min={1}
                  max={100}
                  step={1}
                  className="w-28"
                  placeholder="10"
                  value={formData.mde ?? ''}
                  onChange={(e) =>
                    setFormData(prev => ({ ...prev, mde: e.target.value === '' ? null : Number(e.target.value) }))
                  }
                />
                <Text>% de cambio relativo</Text>
              </div>
              <Text fontSize="caption" className="mt-1">
                Banda de indiferencia para los resultados: efectos más chicos se reportan como "sin
                diferencia real". Por defecto ±10%.
              </Text>
            </div>
            <div>
              <Label htmlFor="primary-segment">Segmento principal</Label>
              <Select
                id="primary-segment"
                name="primary-segment"
                value={formData.primarySegment || 'overall'}
                onChange={(e) => setFormData(prev => ({ ...prev, primarySegment: e.target.value as 'overall' | 'mobile' | 'desktop' }))}
              >
                <Select.Option value="overall" label="General (todos los dispositivos)" />
                <Select.Option value="mobile" label="Mobile" />
                <Select.Option value="desktop" label="Desktop" />
              </Select>
              <Text fontSize="caption" className="mt-1">
                El segmento sobre el que se decide. Los demás dispositivos se muestran como exploratorios.
              </Text>
            </div>
            <div className="flex items-start gap-3 rounded-md border bg-muted/30 p-3">
              <Checkbox
                name="edit-hard-to-revert"
                id="edit-hard-to-revert"
                checked={formData.hardToRevert === true}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, hardToRevert: e.target.checked === true }))
                }
              />
              <div className="space-y-1">
                <Label htmlFor="edit-hard-to-revert" className="cursor-pointer font-medium">
                  Cambio difícil de revertir
                </Label>
                <Text fontSize="caption">
                  Deshabilita el modo Ágil y recomienda esperar un resultado sólido antes de aplicar.
                </Text>
              </div>
            </div>
            <RiskLevelSelector
              value={formData.riskLevel || 'balanced'}
              onChange={(level) => setFormData(prev => ({ ...prev, riskLevel: level }))}
              recommended={formData.hardToRevert ? 'safe' : 'balanced'}
              hardToRevert={formData.hardToRevert === true}
              name="edit-risk-level"
            />
            <div className="flex items-start gap-3 rounded-md border bg-muted/30 p-3">
              <Checkbox
                name="edit-is-negative"
                id="edit-is-negative"
                checked={formData.isNegative === true}
                onChange={(e) =>
                  setFormData(prev => ({ ...prev, isNegative: e.target.checked === true }))
                }
              />
              <div className="space-y-1">
                <Label htmlFor="edit-is-negative" className="cursor-pointer font-medium">
                  Test negativo
                </Label>
                <Text fontSize="caption">
                  Invierte control y tratamiento en los cálculos. Úsalo cuando el cambio bajo prueba ya está en producción y el experimento lo oculta.
                </Text>
              </div>
            </div>
           </div>
          </Card.Body>
        </Card>

        {/* Traffic and Targeting */}
        <Card>
          <Card.Header>
            <Title as="h3">Tráfico y Segmentación</Title>
            <Text>
              Configuración de audiencia y distribución
            </Text>
          </Card.Header>
          <Card.Body>
           <div className="space-y-4">
            <div>
              <Label>División de Tráfico</Label>
              <div className="space-y-3 pt-2">
                <div className="flex items-center justify-between">
                  <Text>Control</Text>
                  <Text className="font-mono">{formData.controlTraffic || 50}%</Text>
                </div>
                <input
                  type="range"
                  min={5}
                  max={95}
                  step={5}
                  value={formData.controlTraffic || 50}
                  onChange={(e) => {
                    const value = parseInt(e.target.value)
                    setFormData(prev => ({
                      ...prev,
                      controlTraffic: value,
                      variantTraffic: 100 - value,
                    }))
                  }}
                  className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                />
                <div className="flex items-center justify-between">
                  <Text>Tratamiento</Text>
                  <Text className="font-mono">{formData.variantTraffic || 50}%</Text>
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="urlPatterns">Patrones de URL</Label>
              <div className="flex gap-2">
                <Input
                  id="urlPatterns"
                  value={formData.urlPatterns || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, urlPatterns: e.target.value }))}
                  placeholder="Ej: /products/*, /collections/*"
                  className="flex-1"
                />
                <Button
                  type="button"
                  appearance="neutral"
                  onClick={() => handlePickSelector((selector) => {
                    // For URL patterns, we might want to extract path from selector context
                    // For now, just inform the user
                    toast({
                      title: 'Selector Selected',
                      description: `Selected: ${selector}. You can use this in targeting.`,
                    })
                  })}
                  className="shrink-0"
                  title="Pick element to help determine URL"
                >
                  <Icon source={<ColorPaletteIcon />} color="currentColor" />
                </Button>
              </div>
            </div>

            <div>
              <Label>Dispositivos</Label>
              <div className="flex gap-4 pt-2">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    name="desktop"
                    id="desktop"
                    label="Desktop"
                    checked={formData.devices?.desktop || false}
                    onChange={(e) =>
                      setFormData(prev => ({
                        ...prev,
                        devices: {
                          desktop: !!e.target.checked,
                          mobile: prev.devices?.mobile ?? true,
                        }
                      }))
                    }
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    name="mobile"
                    id="mobile"
                    label="Mobile"
                    checked={formData.devices?.mobile || false}
                    onChange={(e) =>
                      setFormData(prev => ({
                        ...prev,
                        devices: {
                          desktop: prev.devices?.desktop ?? true,
                          mobile: !!e.target.checked,
                        }
                      }))
                    }
                  />
                </div>
              </div>
            </div>

            <div>
              <Label htmlFor="exclusions">Exclusiones</Label>
              <Input
                id="exclusions"
                value={formData.exclusions || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, exclusions: e.target.value }))}
                placeholder="Ej: user_id:admin*"
              />
            </div>
           </div>
          </Card.Body>
        </Card>

        {/* Funnel Configuration */}
        <Card>
          <Card.Header>
            <div className="flex items-center gap-2">
              <Icon source={<StepsIcon />} />
              <Title as="h3">Configuración de Funnel</Title>
            </div>
            <Text>
              Define qué pasos del funnel se usarán para rastrear conversiones
            </Text>
          </Card.Header>
          <Card.Body>
           <div className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center space-x-2">
                <Radio
                  name="funnel-option"
                  id="funnel-default"
                  label="Funnel por Defecto"
                  value="default"
                  checked={(formData.funnelOption || 'default') === 'default'}
                  onChange={(e) => setFormData(prev => ({ ...prev, funnelOption: e.target.value as 'default' | 'custom' }))}
                />
              </div>
              <div className="flex items-center space-x-2">
                <Radio
                  name="funnel-option"
                  id="funnel-custom"
                  label="Funnel Personalizado"
                  value="custom"
                  checked={(formData.funnelOption || 'default') === 'custom'}
                  onChange={(e) => setFormData(prev => ({ ...prev, funnelOption: e.target.value as 'default' | 'custom' }))}
                />
              </div>
            </div>

            {formData.funnelOption === 'custom' && (
              <div className="space-y-2 pt-2 border-t">
                <Label className="text-sm font-medium">Selecciona los pasos del funnel</Label>
                <div className="space-y-2">
                  {availableFunnelSteps
                    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
                    .map(step => (
                    <div key={step.id} className="flex items-center space-x-2">
                      <Checkbox
                        name={`step-${step.id}`}
                        id={`step-${step.id}`}
                        label={step.name}
                        checked={formData.funnelSteps?.includes(step.id) || false}
                        onChange={(e) => {
                          const checked = e.target.checked
                          const currentSteps = formData.funnelSteps || []
                          const updatedSteps = checked
                            ? [...currentSteps, step.id]
                            : currentSteps.filter(s => s !== step.id)
                          setFormData(prev => ({ ...prev, funnelSteps: updatedSteps }))
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
           </div>
          </Card.Body>
        </Card>
      </div>

      {/* Timing Configuration */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Icon source={<CalendarIcon />} />
            <Title as="h3">Configuración de Tiempo</Title>
          </div>
          <Text>
            Programa el inicio y finalización del experimento
          </Text>
        </Card.Header>
        <Card.Body>
         <div className="space-y-4">
          {/* Start Time */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="enable-start-time">Programar Inicio</Label>
              <Toggle
                name="enable-start-time"
                id="enable-start-time"
                active={enableStartTime}
                onChange={(e) => setEnableStartTime(e.target.checked)}
              />
            </div>
            {enableStartTime && (
              <div>
                <Label htmlFor="start-datetime" className="text-sm text-muted-foreground">
                  Fecha y hora de inicio
                </Label>
                <Input
                  id="start-datetime"
                  type="datetime-local"
                  value={startDateTime}
                  onChange={(e) => setStartDateTime(e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
          </div>

          {/* End Time */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label htmlFor="enable-end-time">Programar Fin</Label>
              <Toggle
                name="enable-end-time"
                id="enable-end-time"
                active={enableEndTime}
                onChange={(e) => setEnableEndTime(e.target.checked)}
              />
            </div>
            {enableEndTime && (
              <div>
                <Label htmlFor="end-datetime" className="text-sm text-muted-foreground">
                  Fecha y hora de finalización
                </Label>
                <Input
                  id="end-datetime"
                  type="datetime-local"
                  value={endDateTime}
                  onChange={(e) => setEndDateTime(e.target.value)}
                  className="mt-1"
                />
              </div>
            )}
          </div>
         </div>
        </Card.Body>
      </Card>

      {/* Session Limit */}
      <Card>
        <Card.Header>
          <div className="flex items-center gap-2">
            <Icon source={<ClockIcon />} />
            <Title as="h3">Límite de Sesiones</Title>
          </div>
          <Text>
            Limita el número total de sesiones del experimento
          </Text>
        </Card.Header>
        <Card.Body>
         <div className="space-y-4">
          <div className="flex items-center justify-between">
            <Label htmlFor="enable-session-limit">Limitar Sesiones</Label>
            <Toggle
              name="enable-session-limit"
              id="enable-session-limit"
              active={enableSessionLimit}
              onChange={(e) => setEnableSessionLimit(e.target.checked)}
            />
          </div>
          {enableSessionLimit && (
            <div className="space-y-2">
              <Label htmlFor="session-limit">Número de Sesiones</Label>
              <Input
                id="session-limit"
                type="number"
                min={100}
                value={sessionLimit}
                onChange={(e) => setSessionLimit(parseInt(e.target.value) || 0)}
                placeholder="Ej: 1000"
              />
              {experiment?.current_sessions && (
                <div className="text-sm text-muted-foreground">
                  Sesiones actuales: {experiment.current_sessions.toLocaleString('es-ES')}
                </div>
              )}
            </div>
          )}
         </div>
        </Card.Body>
      </Card>

      {/* Variant Configuration */}
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <div>
              <Title as="h3">Configuración de Variante</Title>
              <Text>
                Personaliza la variante de tratamiento para {experiment.type}
              </Text>
            </div>
            <Button
              appearance="neutral"
              onClick={handleLaunchVisualEditor}
              className="flex items-center gap-2"
            >
              <Icon source={<ColorPaletteIcon />} color="currentColor" />
              Editor Visual
            </Button>
          </div>
        </Card.Header>
        <Card.Body>
          {variantConfig ? (
            <div className="space-y-4">
              <div className="rounded-lg border p-4 bg-muted/50">
                <Title as="h4" className="mb-2">Configuración Guardada</Title>
                <div className="text-sm text-muted-foreground space-y-1">
                  <p><strong>Selector:</strong> {variantConfig.selector}</p>
                  <p><strong>Modificaciones:</strong> {variantConfig.modifications.length}</p>
                  {variantConfig.css && (
                    <details className="mt-2">
                      <summary className="cursor-pointer font-semibold">Ver CSS generado</summary>
                      <pre className="mt-2 p-2 bg-background rounded text-xs overflow-x-auto">
                        {variantConfig.css}
                      </pre>
                    </details>
                  )}
                </div>
              </div>
              {renderVariantConfig()}
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="mx-auto mb-3 flex justify-center text-muted-foreground">
                <Icon source={<ColorPaletteIcon />} />
              </div>
              <Text className="mb-4">
                No hay configuración de variante. Usa el Editor Visual para crear una.
              </Text>
              {renderVariantConfig()}
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Visual Selector Picker Modal */}
      {showSelectorPicker && experiment?.targeting?.url_patterns && (
        <VisualSelectorPicker
          storeUrl={experiment.targeting.url_patterns.split(',')[0] || 'https://ejemplo.mitiendanube.com'}
          onSelect={handleSelectorSelected}
          onCancel={handleSelectorPickerCancel}
        />
      )}
    </div>
  )
}